import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-date-range';
import { subDays } from 'date-fns';
import InfoCard from './InfoCard';
import HistoricalReportTable from './HistoricalReportTable';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Popover from '@mui/material/Popover';
import './DateRangeCustomStyles.css';
import useSettings from '../../hooks/useSettings';
import { IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { CopyAllOutlined } from '@mui/icons-material';
import SendersModal from '../../components/modal-senders-emails/SendersModal';
import { toast } from 'react-toastify';
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';
import Select from 'react-select';
import { getCustomSelectStyles } from '../../common/styles-select';
import apiClient from '../../config/apiClient';
import Loading from '../../components/Loading';
import ContactPageIcon from '@mui/icons-material/ContactPage';
interface HistoricalReport {
  reportId: number;
  tenantName: string;
  reportDate: string;
  SharedMailbox: number;
  UserMailbox: number;
  RoomMailbox: number;
  ServiceMailbox: number;
  OutOfScope: number;
  ADMAccount: number;
  scopeChangeStatus: string;
  changePercentage: number;
  total: number;
  threshold: number;
}

const DashboardHistoricalSwitcher: React.FC = () => {
  const [historicalReports, setHistoricalReports] = useState<HistoricalReport[]>([]);
  const [firstReport, setFirstReport] = useState<HistoricalReport>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenants, setTenants] = useState<{ id: number; name: string, threshold: any }[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
    key: 'selection'
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { themeMode } = useSettings();
  const [openModal, setOpenModal] = useState(false);
  const [tenantThreshold, setTenantThreshold] = useState<number | null>(null);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);


  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await apiClient.get('/tenants');
        setTenants(response.data);
        setSelectedTenant(response.data[0]?.id || '');
        setTenantThreshold(response.data[0]?.threshold || '');
      } catch (err) {
        setError('Error fetching tenants');
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, []);

  useEffect(() => {
    const fetchHistoricalReports = async () => {
      if (selectedTenant && dateRange.startDate && dateRange.endDate) {
        setLoading(true);
        try {
          const startDate = dateRange.startDate.toISOString().split('T')[0];
          const endDate = dateRange.endDate.toISOString().split('T')[0];

          const response = await apiClient.get(
            `/reports/${selectedTenant}?startDate=${startDate}&endDate=${endDate}`
          );

          setHistoricalReports(response.data);
        } catch (err) {
          setError('Error fetching historical reports');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchHistoricalReports();
  }, [selectedTenant, dateRange]);

  useEffect(() => {
    const fetchFirstReport = async () => {
      if (selectedTenant) {
        setLoading(true);
        try {
          const response = await apiClient.get(`/report/first/${selectedTenant}`);
          setFirstReport(response.data);
        } catch (err) {
          setError('Error fetching first report');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchFirstReport();
  }, [selectedTenant]);

  if (loading) return <Loading />;
  if (error) return <p>{error}</p>;

  const handleTenantChange = (option: any) => {
    const selectTenant = tenants.find(tnt => tnt.id.toString() === option.value.toString());

    setTenantThreshold(selectTenant?.threshold || '');
    setSelectedTenant(option.value.toLocaleString());
  };

  const handleSelect = (ranges: any) => {
    setDateRange(ranges.selection);

    if (ranges.selection.startDate && ranges.selection.endDate) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const combinedSelectStyles = getCustomSelectStyles(themeMode);

  const handleExcel = async () => {
    if (selectedTenant && dateRange.startDate && dateRange.endDate) {
      setLoading(true);
      try {
        const startDate = dateRange.startDate.toISOString().split('T')[0];
        const endDate = dateRange.endDate.toISOString().split('T')[0];

        const response = await apiClient.get(
          `/excel/${selectedTenant}?startDate=${startDate}&endDate=${endDate}`,
          { responseType: 'blob' }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'report.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('Excel downloaded successfully!', { position: 'top-right' });
      } catch (err) {
        setError('Error generating historical report');
        toast.error('Failed to download Excel report!', { position: 'top-right' });
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn('Please select a tenant and date range.', { position: 'top-right' });
    }
  };

  const handleExcelUsers = async (reportId: number) => {
    if (selectedTenant && dateRange.startDate && dateRange.endDate) {
      setLoading(true);
      try {

        const response = await apiClient.get(
          `/usersReport/${selectedTenant}/${reportId}`,
          { responseType: 'blob' }
        );

        const contentDisposition = response.headers['content-disposition'];
        const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
        const fileName = fileNameMatch ? fileNameMatch[1] : 'report.xlsx';
        console.log('header', contentDisposition);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('Excel downloaded successfully!', { position: 'top-right' });
      } catch (err) {
        setError('Error generating historical report');
        toast.error('Failed to download Excel report!', { position: 'top-right' });
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn('Please select a tenant and date range.', { position: 'top-right' });
    }
  };

  const handleCopyToClipboard = () => {
    if (historicalReports.length > 0) {
      const headers = "Report ID\tTenant Name\tDate\tShared Mailbox\tUser Mailbox\tRoom Mailbox\tService Mailbox\tOut of Scope\tADM Account\tScope Change Status\tChange Percentage\n";

      const rows = historicalReports.map(report =>
        `${report.reportId}\t${report.tenantName}\t${new Date(report.reportDate).toLocaleDateString('es-ES')}\t${report.SharedMailbox}\t${report.UserMailbox}\t${report.RoomMailbox}\t${report.ServiceMailbox}\t${report.OutOfScope}\t${report.ADMAccount}\t${report.scopeChangeStatus}\t${report.changePercentage.toFixed(0)}`
      ).join('\n');

      const tableText = headers + rows;

      navigator.clipboard.writeText(tableText)
        .then(() => {
          toast.success('Table copied to clipboard!', {
            position: 'top-right',
          });
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          toast.error('Failed to copy text to clipboard!', {
            position: 'top-right',
          });
        });
    } else {
      toast.warn('No data available to copy', {
        position: 'top-right',
      });
    }
  };

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Historical Reports</h2>
        <div className="flex items-center">
          <span className="mr-2">Cards</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={viewMode === 'table'}
              onChange={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-700"></div>
          </label>
          <span className="ml-2">Table</span>
        </div>
      </div>

      <div className="mb-6 flex justify-center items-center space-x-4">
        <div className="mb-3 flex justify-center items-center space-x-4">
          <label className="mr-2 text-lg">Select Tenant:</label>
        </div>
        <div className="mb-3 flex justify-center items-center space-x-4">
          <Select
            onChange={(option) => handleTenantChange(option)}
            options={tenantOptions}
            styles={combinedSelectStyles}
            value={tenantOptions.find(option => option.value.toString() === selectedTenant)}
          />
        </div>
        <div>
          <button
            onClick={handleClick}
            className="border p-2 rounded-lg"
          >
            {`${dateRange.startDate.toLocaleDateString('es-ES')} - ${dateRange.endDate.toLocaleDateString('es-ES')}`}
          </button>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <DateRange
              editableDateInputs={true}
              onChange={handleSelect}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={[dateRange]}
              className="border rounded-lg mt-2"
            />
          </Popover>
        </div>
      </div>
      <div className="mb-2 flex justify-between items-center">
        <div className="flex items-center">
          <DataThresholdingIcon className="mr-2" />
          <span className="font-bold text-lg">Current Billing Threshold: {tenantThreshold || firstReport?.total}</span>
        </div>

        <div>
          <SendersModal open={openModal} handleClose={handleClose} selectedTenant={selectedTenant} dateRange={dateRange} />

          <Tooltip title="Download Excel" placement="top" arrow>
            <IconButton onClick={handleExcel}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Send email" placement="top" arrow>
            <IconButton onClick={handleOpen}>
              <MailOutlineIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy to Clipboard" placement="top" arrow>
            <IconButton onClick={handleCopyToClipboard}>
              <CopyAllOutlined />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {viewMode === 'cards' ? (
        historicalReports.map((report, index) => (
          <div key={index} className="mb-9">
            <h3 className="text-lg font-semibold mb-4">
              Report of Date {new Date(report.reportDate).toLocaleDateString('es-ES')} - {new Date(report.reportDate).toLocaleTimeString()}
              <Tooltip title="Download Users details" placement="top" arrow>
                <IconButton onClick={() => handleExcelUsers(report.reportId)}>
                  <ContactPageIcon />
                </IconButton>
              </Tooltip>
            </h3>
            <div className="flex flex-wrap justify-center">
              <InfoCard classification="SharedMailbox" count={report.SharedMailbox} />
              <InfoCard classification="UserMailbox" count={report.UserMailbox} />
              <InfoCard classification="RoomMailbox" count={report.RoomMailbox} />
              <InfoCard classification="ServiceMailbox" count={report.ServiceMailbox} />
              <InfoCard classification="OutOfScope" count={report.OutOfScope} />
              <InfoCard classification="ADMAccount" count={report.ADMAccount} />
              <InfoCard classification="scopeChangeStatus" count={report.scopeChangeStatus} />
              <InfoCard classification="changePercentage" count={report.changePercentage || 0} />
            </div>
          </div>
        ))
      ) : (
        <HistoricalReportTable reports={historicalReports} selectedTenant={selectedTenant}/>
      )}
    </div>
  );
};

export default DashboardHistoricalSwitcher;
